import type { InputProps } from "react-aria-components";

import { X } from "lucide-react";
import React from "react";
import { composeRenderProps, Group } from "react-aria-components";
import { twMerge } from "tailwind-merge";

import { Button } from "@/components/button";
import { Input } from "@/components/field";
import { focusRingStyle } from "@/components/utils";

interface UrlInputProps extends InputProps {
  onClear?: () => void;
  showClearButton?: boolean;
}

export const UrlInput = React.forwardRef<HTMLInputElement, UrlInputProps>(
  function UrlInput({ onClear, showClearButton, ...inputProps }, ref) {
    return (
      <Group
        className={composeRenderProps(
          "",
          (className, { isFocusWithin, isInvalid }) =>
            twMerge(
              "isolate items-center rounded-md border outline-hidden border-border-primary bg-bg-primary h-[46px]",
              "grid grid-cols-[auto_1fr_auto] transition-colors",
              isInvalid && "border-border-error",
              isFocusWithin && focusRingStyle,
              className,
            ),
        )}
        data-ui="control"
      >
        <span className="flex items-center px-3 h-[44px] text-sm text-text-secondary border-r border-border-primary bg-bg-secondary-subtle select-none rounded-l-md">
          https://
        </span>

        <Input
          {...inputProps}
          className={twMerge(
            "h-full border-0 rounded-none bg-transparent px-3 focus:ring-0 focus:outline-none col-start-2",
            showClearButton ? "pr-10" : "pr-3",
          )}
          ref={ref}
        />

        {showClearButton && onClear && (
          <Button
            className="z-10 col-start-3 row-start-1 -mr-[2px] place-self-center"
            isIconOnly
            onPress={onClear}
            size="sm"
            variant="plain"
          >
            <X
              aria-label="Clear URL"
              className="rounded-full border-3 border-border-tertiary bg-bg-tertiary text-fg-quaternary"
              size={22}
            />
          </Button>
        )}
      </Group>
    );
  },
);
